import React, { useMemo, useState, useEffect } from 'react';
import { DeckNavigation } from '../../shared/components/DeckNavigation';
import { SlideShell } from '../../shared/components/SlideShell';
import { TranslationProvider, useTranslation } from '../../shared/contexts/TranslationContext';
import { LanguageToggle } from '../../shared/components/LanguageToggle';
import { AdminLink } from '../../shared/components/AdminLink';
import './EzERPPartners.css';

import {
    Slide1, Slide1Image,
    Slide2, Slide2Image,
    Slide3,
    Slide4,
    Slide5,
    Slide6,
    Slide7,
    Slide8,
    Slide9, Slide9Image,
    Slide10,
    Slide11, Slide11Image
} from './components/EzERPPartnersSlides';

const EzERPPartnersDeckInner = ({ lang, setLang }) => {
    const [active, setActive] = useState(0);
    const [showComplete, setShowComplete] = useState(false);
    const deckRef = React.useRef(null);
    
    const { t: contextT } = useTranslation();

    const t = useMemo(() => {
        return (key) => {
            const val = contextT(key);
            if (typeof val === 'string') {
                return val.replace(/\{currency\}/g, 'R$').replace(/\{symbol\}/g, 'R$');
            }
            return val || key;
        };
    }, [contextT]);

    const scrollTo = (idx) => {
        setActive(idx);
    };

    useEffect(() => {
        let isThrottled = false;
        
        const handleWheel = (e) => {
            if (isThrottled || showComplete) return;
            
            const direction = Math.sign(e.deltaY);
            if (direction > 0) {
                if (active < 10) {
                    setActive(prev => prev + 1);
                } else {
                    setShowComplete(true);
                }
            } else if (direction < 0 && active > 0) {
                setActive(prev => prev - 1);
            }
            
            isThrottled = true;
            setTimeout(() => {
                isThrottled = false;
            }, 800); // 800ms throttle to match the CSS transition duration
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => window.removeEventListener('wheel', handleWheel);
    }, [active, showComplete]);

    useEffect(() => {
        document.title = "EzERP Partners | Executive Presentation";
    }, []);

    return (
        <div className="theme-ezerp-partners min-h-screen bg-[var(--bg-dark)] text-white font-sans antialiased">
            <LanguageToggle lang={lang} setLang={setLang} />
            <AdminLink />
            <div className="watermark" style={{ display: 'block', position: 'fixed', top: '2rem', left: '2rem', zIndex: 100, color: 'rgba(255,255,255,0.1)', fontFamily: 'Manrope, sans-serif', fontSize: '0.8rem', letterSpacing: '4px', textTransform: 'uppercase' }}>
                {t('watermark')}
            </div>
            
            <DeckNavigation count={11} active={active} onJump={scrollTo} onComplete={() => setShowComplete(true)} variant="dots" />

            <main ref={deckRef} className="presentation-container no-scrollbar">
                <SlideShell index={0} theme="ezerppartners" className={`dark ${active === 0 ? 'active' : ''}`}>
                    <Slide1 t={t} />
                    <Slide1Image />
                </SlideShell>

                <SlideShell index={1} theme="ezerppartners" className={`dark ${active === 1 ? 'active' : ''}`}>
                    <Slide2 t={t} />
                    <Slide2Image />
                </SlideShell>

                <SlideShell index={2} theme="ezerppartners" className={`dark full-width ${active === 2 ? 'active' : ''}`}>
                    <Slide3 t={t} />
                </SlideShell>

                <SlideShell index={3} theme="ezerppartners" className={`dark full-width ${active === 3 ? 'active' : ''}`}>
                    <Slide4 t={t} />
                </SlideShell>

                <SlideShell index={4} theme="ezerppartners" className={`dark full-width ${active === 4 ? 'active' : ''}`}>
                    <Slide5 t={t} />
                </SlideShell>

                <SlideShell index={5} theme="ezerppartners" className={`full-width ${active === 5 ? 'active' : ''}`} bgStyle={{ padding: 0 }}>
                    <Slide6 t={t} />
                </SlideShell>

                <SlideShell index={6} theme="ezerppartners" className={`dark ${active === 6 ? 'active' : ''}`} bgStyle={{ background: '#1c1612' }}>
                    <Slide7 t={t} />
                </SlideShell>

                <SlideShell index={7} theme="ezerppartners" className={`dark full-width ${active === 7 ? 'active' : ''}`}>
                    <Slide8 t={t} />
                </SlideShell>

                <SlideShell index={8} theme="ezerppartners" className={`dark ${active === 8 ? 'active' : ''}`}>
                    <Slide9 t={t} />
                    <Slide9Image />
                </SlideShell>

                <SlideShell index={9} theme="ezerppartners" className={`dark full-width ${active === 9 ? 'active' : ''}`}>
                    <Slide10 t={t} />
                </SlideShell>

                <SlideShell index={10} theme="ezerppartners" className={`dark ${active === 10 ? 'active' : ''}`}>
                    <Slide11 t={t} />
                    <Slide11Image />
                </SlideShell>
            </main>

            {showComplete && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(10px)',
                    zIndex: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        background: 'rgba(26, 26, 26, 0.95)',
                        padding: '3rem',
                        border: '1px solid var(--ph-accent, #e8d5b8)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        maxWidth: '450px',
                        width: '90%',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
                    }}>
                        <h2 style={{ color: 'var(--ph-accent, #e8d5b8)', fontFamily: 'Prata, serif', fontSize: '2.5rem', marginBottom: '1rem' }}>Presentation Complete</h2>
                        <p style={{ margin: '1rem 0 2rem 0', opacity: 0.8, fontFamily: 'Manrope, sans-serif', fontSize: '1.1rem', color: '#fff' }}>Thank you for exploring EzERP.</p>
                        <button 
                            className="btn" 
                            style={{ width: 'auto', margin: '0 auto', padding: '0 2rem', borderRadius: '50px', height: '3rem', fontWeight: 600 }}
                            onClick={() => {
                                setShowComplete(false);
                                scrollTo(0);
                            }}
                        >
                            Restart
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const EzERPPartnersDeck = () => {
    const [lang, setLang] = useState(() => {
        if (typeof window !== "undefined") {
            const stored = localStorage.getItem("ezerp_partners_lang");
            if (stored === "pt" || stored === "en" || stored === "es") return stored;
            const nav = (navigator.language || "en").toLowerCase();
            if (nav.startsWith("es")) return "es";
            return nav.startsWith("pt") ? "pt" : "en";
        }
        return "en";
    });

    useEffect(() => {
        localStorage.setItem("ezerp_partners_lang", lang);
        document.documentElement.setAttribute("lang", lang);
    }, [lang]);

    return (
        <TranslationProvider deck="ezerp-partners" lang={lang}>
            <EzERPPartnersDeckInner lang={lang} setLang={setLang} />
        </TranslationProvider>
    );
};

export default EzERPPartnersDeck;

